import {
    Attachment,
    Exercise,
    ExerciseAttachments,
    ExerciseTableData,
    FailedExercise,
    Method,
    PlanExercise,
    PlanExerciseTableData,
    Stage,
    Useraccount
} from "../../../../types/domain";
import React, {useEffect, useMemo, useState} from "react";
import {ExerciseCreatePanel} from "../utils/ExerciseCreatePanel";
import {MantineReactTable, MRT_ColumnDef, MRT_Row, useMantineReactTable} from "mantine-react-table";
import {MRT_Localization_DE} from "mantine-react-table/locales/de";
import {ActionIcon, Box, Tooltip} from "@mantine/core";
import {IconAlertTriangle, IconEye, IconPencil, IconTrash} from "@tabler/icons-react";
import {generatePlanExerciseTableData} from "../utils/helperFunctions";
import UseraccountService from "../../../../services/useraccount.service";
import ExerciseService from "../../../../services/exercise.service";
import {UpdateExerciseModal} from "../utils/modal/UpdateExerciseModal";
import MethodService from "../../../../services/method.service";
import {loadPreviewItems, PreviewItem} from "../../../../types/PreviewTypes";

import "../../../../css/components/ExercisesForm.css"
import "../../../../css/components/Modals.css"
import {ShowAttachmentModal} from "../utils/modal/ShowAttachmentModal";


/**
 * @author KMU
 * Form element to create the new exercises
 */

type ExercisesData = {
    newExercises: Array<PlanExercise>,
    topic: string,
    newAttachments: Array<ExerciseAttachments>,
    user: Useraccount,
    stages: Array<Stage>
}

//parent property that will be used in this element
type ExercisesFormProps = ExercisesData & {
    updateFields: (fields: Partial<ExercisesData>) => void,
    failedExercises: Array<FailedExercise>,
    dbStages: Array<Stage>
}

export function ExercisesForm({
                                  newExercises, topic, newAttachments, user, stages,
                                  updateFields, failedExercises, dbStages
                              }: ExercisesFormProps) {

    const [data, setData] = useState<Array<PlanExerciseTableData>>([]);
    const [allExercises, setAllExercises] = useState<Array<Exercise>>([]);
    const [userExercises, setUserExercises] = useState<Array<Exercise>>([]);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [exerciseToEdit, setExerciseToEdit] = useState<Exercise | null>(null);
    const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([]);
    const [previewAttachments, setPreviewAttachments] = useState<string[] | null>(null);
    const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false);
    const [previewItems, setPreviewItems] = useState<Array<PreviewItem>>([])
    const [allMethods, setAllMethods] = useState<Array<Method>>([]);

    useEffect(() => {
        MethodService.getAll()
            .then((response: any) => {
                setAllMethods(response.data);
            });
    }, []);

    //definition of table columns
    const columns = useMemo<MRT_ColumnDef<PlanExerciseTableData>[]>(
        () => [
            {
                accessorKey: 'name',
                header: 'Übung/Tätigkeit'
            },
            {
                accessorKey: 'description',
                header: 'Beschreibung',
                size: 300
            },
            {
                accessorKey: 'method',
                header: 'Methode',
                size: 50,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                }
            },
            {
                accessorKey: 'material',
                header: 'Material'
            },
            {
                accessorKey: 'stages',
                header: 'Stadien',
                size: 50,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                }
            },
            {
                accessorKey: 'attachments',
                header: 'Anhänge',
                size: 50,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({row}) => {
                    const attachCount = row.original.attachments;
                    return (
                        <>
                            {attachCount > 0 ? (
                                <Tooltip label="Anhänge anzeigen">
                                    <div className="tableAttachment">
                                        <span style={{textAlign: 'center'}}>
                                            {attachCount}
                                        </span>
                                        {row.original.paths.length > 0 ?
                                            <ActionIcon
                                                color="blueish"
                                                onClick={() => {
                                                    setPreviewAttachments(row.original.paths);
                                                    setShowAttachmentModal(true);
                                                }}>
                                                <IconEye/>
                                            </ActionIcon> : <></>
                                        }
                                    </div>
                                </Tooltip>
                            ) : <span>0</span>
                            }
                        </>
                    )
                }
            }
        ], []
    );

    /**
     * React to change of new exercises
     */
    useEffect(() => {
        // init table
        let tableData = generatePlanExerciseTableData(newExercises, newAttachments, stages);
        setData([...tableData]);

        // set list with all exercises
        ExerciseService.getAll().then((response: any) => {
            setAllExercises(response.data);
        }).catch((e: Error) => {
            console.log(e);
        });
    }, [newExercises, newAttachments]);

    /**
     * React to user change (so only in beginning)
     */
    useEffect(() => {
        if (user) {
            UseraccountService.getExercises(user.id).then((response: any) => {
                setUserExercises(response.data);
            }).catch((e: Error) => {
                console.log(e);
            });
        }
    }, [user]);

    /**
     * React to preview interaction and set preview files
     */
    useEffect(() => {
        if (!previewAttachments || previewAttachments.length === 0) return;
        async function load() {
            const items = await loadPreviewItems(previewAttachments!);
            setPreviewItems(items);
        }
        load();
    }, [previewAttachments]);

    /**
     * React to preview-modal for better visibility
     */
    useEffect(() => {
        if (showAttachmentModal) {
            setTimeout(() => {
                window.dispatchEvent(new Event("resize"));
            }, 50);
        }
    }, [showAttachmentModal]);

    useEffect(() => {
        // force table to re-evaluate row styles when failedExercises changes
        setData(prev => [...prev]);
    }, [failedExercises]);

    /**
     * Delete exercise from list of new exercises
     * @param name exercise name
     * @param desc exercise description
     * @param material exercise material
     */
    function deleteExercise(name: string, desc: string, material: string) {
        newExercises = newExercises.filter(e => e.exercise.name !== name || e.exercise.description !== desc || e.exercise.material !== material)
        newAttachments = newAttachments.filter(a => a.exercise.name !== name || a.exercise.description !== desc || a.exercise.material !== material)
        updateFields({newExercises: newExercises, newAttachments: newAttachments})
    }

    /**
     * Reorder exercises according to table data
     * @param tableData exercises in table data form
     * @param exercises new exercises
     */
    function reOrderExercises(tableData: Array<ExerciseTableData>, exercises: Array<PlanExercise>) {
        let reorderedExes: Array<PlanExercise> = []
        tableData.forEach(row => {
            const exercise = exercises.find(e => e.exercise.name === row.name
                && e.exercise.description === row.description && e.exercise.material === row.material)
            if (exercise) {
                reorderedExes.push(exercise);
            }
        })
        updateFields({newExercises: reorderedExes});
    }

    async function handleUpdateExercise(updated: {
        name: string,
        description: string,
        material: string
    }) {
        if (!exerciseToEdit) {
            return;
        }

        let dbMethods = []
        const dbMethod = (await MethodService.getByCode(exerciseToEdit.methods[0].code)).data
        if (dbMethod) {
            dbMethods.push(dbMethod)
        } else {
            return; // no method found, cannot update exercise
        }

        const index = newExercises.findIndex(e =>
            e.exercise.name === exerciseToEdit.name &&
            e.exercise.description === exerciseToEdit.description &&
            e.exercise.material === exerciseToEdit.material
        );

        //Preserve the PlanExercise's existing attachment selection
        const existingPlanExercise = index !== -1 ? newExercises[index] : null;

        //Check if exercise with same name, description and material exists in DB
        let dbExercise = (await ExerciseService.getByNameDescMat(updated.name, updated.description, updated.material)).data;
        if (typeof dbExercise === 'string') { // not in DB
            dbExercise = {
                id: 0,
                name: updated.name,
                description: updated.description,
                material: updated.material,
                methods: dbMethods,
                stages: [],
                topics: [],
                attachments: existingPlanExercise?.attachments ?? [],
                user: undefined,
                parentId: exerciseToEdit.id
            };
        }



        //Replace the exercise in the new exercises list
        const updatedExercises = [...newExercises];
        let newPlanExercise: PlanExercise = {
            id: existingPlanExercise?.id ?? 0,
            exercise: dbExercise,
            attachments: existingPlanExercise?.attachments ?? [],   // keep original selection, not the full list
            interest: existingPlanExercise?.interest,
            execution: existingPlanExercise?.execution,
            position: existingPlanExercise?.position ?? updatedExercises.length + 1
        }
        if (index !== -1) {
            updatedExercises.splice(index, 1, newPlanExercise);
        } else {
            updatedExercises.push(newPlanExercise);
        }

        // Re-key the matching newAttachments entry to the updated exercise identity.
        const updatedAttachments = newAttachments.map(a => {
            if (a.exercise.name === exerciseToEdit.name &&
                a.exercise.description === exerciseToEdit.description &&
                a.exercise.material === exerciseToEdit.material) {
                return {...a, exercise: dbExercise};
            }
            return a;
        });

        updateFields({newExercises: updatedExercises, newAttachments: updatedAttachments});
    }

    const table = useMantineReactTable<PlanExerciseTableData>({
        columns,
        data,
        enableColumnActions: false,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableRowActions: true,
        positionActionsColumn: "last",
        renderRowActions: ({row}) => {
            const failedEntry = failedExercises.find(f =>
                f.exercise.name === row.original.name &&
                f.exercise.description === row.original.description &&
                f.exercise.material === row.original.material
            );

            return <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
                {failedEntry  && (
                    <Tooltip label={failedEntry.reason}>
                        <ActionIcon color="red" variant="transparent">
                            <IconAlertTriangle/>
                        </ActionIcon>
                    </Tooltip>
                )}
                <Tooltip label="Übung bearbeiten">
                    <ActionIcon color="blueish" onClick={() => {
                        const planExercise = newExercises.find(e =>
                            e.exercise.name === row.original.name &&
                            e.exercise.description === row.original.description &&
                            e.exercise.material === row.original.material
                        );
                        setExerciseToEdit(planExercise?.exercise ?? null);
                        setSelectedAttachments(planExercise?.attachments ?? []);
                        setShowUpdateModal(true);
                    }}>
                        <IconPencil/>
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Übung entfernen">
                    <ActionIcon color="red"
                                onClick={() => deleteExercise(row.original.name, row.original.description, row.original.material)}>
                        <IconTrash/>
                    </ActionIcon>
                </Tooltip>
            </Box>;
        },
        localization: MRT_Localization_DE,
        enableRowOrdering: true,
        mantineRowDragHandleProps: {
            onDragEnd: () => {
                const {draggingRow, hoveredRow} = table.getState();
                if (hoveredRow && draggingRow) {
                    data.splice(
                        (hoveredRow as MRT_Row<ExerciseTableData>).index,
                        0,
                        data.splice(draggingRow.index, 1)[0],
                    );
                    setData([...data]);
                    reOrderExercises(data, newExercises);
                }
            },
        },
        enableStickyHeader: true,
        mantineTableContainerProps: {sx: {maxHeight: '500px'}},
        enablePagination: false,
        mantineTableBodyRowProps: ({row}) => {
            const isFailed = failedExercises.some(f =>
                f.exercise.name === row.original.name &&
                f.exercise.description === row.original.description &&
                f.exercise.material === row.original.material
            );
            return {
                sx: isFailed ? {
                    outline: '2px solid red',
                    outlineOffset: '-2px'
                } : {}
            };
        }
    });

    return (
        <>
            <ExerciseCreatePanel newExercises={newExercises} allExercises={allExercises} userExercises={userExercises}
                                 newAttachments={newAttachments} updateFields={updateFields}
                                 allMethods={allMethods} stages={stages} dbStages={dbStages}/>
            <hr></hr>
            <div className="exerciseListTable">
                <h4 className="heading">Geplante Tätigkeiten</h4>
                <MantineReactTable
                    table={table}
                />
            </div>
            <UpdateExerciseModal
                opened={showUpdateModal}
                close={() => {
                    setShowUpdateModal(false);
                    setExerciseToEdit(null);
                    setData([...generatePlanExerciseTableData(newExercises, newAttachments, stages)]);
                }}
                title="Übung aktualisieren"
                exercise={exerciseToEdit}
                selectedAttachments={selectedAttachments}
                onUpdate={handleUpdateExercise}
                newAttachments={newAttachments}
                updateFields={updateFields}
                allStages={dbStages}
                planStages={stages}
            />
            <ShowAttachmentModal opened={showAttachmentModal} close={() => {
                setShowAttachmentModal(false);
                setPreviewAttachments(null);
                setPreviewItems([]);
            }} previewItems={previewItems} />
        </>
    )
}